import { getCareSourceAttribution } from './careSourceAttribution';
import React, { useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { saveKnowledgeItem } from '../knowledgeItems';
import { PROFILE_PLACEMENT_MODES } from '../profileContract/profilePlacementMetadata';
import { getProfilePlacement } from '../profileContract/profilePlacementContract';
import { isAllergySuggestion } from '../../utils/allergyReviewCandidate';
import {
  markCareKnowledgeSourceReviewComplete,
  saveDirectProfileReviewProgress,
} from './careSourceActivityService';
import CollapsibleReviewGroup from './CollapsibleReviewGroup';
import CompactReviewRow from './CompactReviewRow';
import {
  CAPTURE_DESTINATIONS,
  getCaptureSuggestionDestination,
} from './captureDestinationPolicy';

import { getPrimaryProfileItemKeys } from './profileSourceReviewSummary';

const clean = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const PROFILE_CHAPTER_LABELS = Object.freeze({
  communication: 'Communication',
  what_helps: 'What Helps',
  watch_for: 'Watch For',
  daily_life: 'Daily Life',
  safety: 'Safety',
  health_care: 'Health & Care',
});
export const getProfileChapterLabel = (chapterId = '') => (
  PROFILE_CHAPTER_LABELS[clean(chapterId)] || 'Profile'
);
const chapterLabel = getProfileChapterLabel;
const NON_ACTIONABLE_REVIEW_STATUSES = new Set([
  'added',
  'already_known',
  'archived',
  'auto_applied',
  'completed',
  'duplicate',
  'hidden',
  'ignored',
  'resolved',
  'skipped',
  'unchanged',
]);

export const getDirectProfileReviewItems = (source = {}, suggestions = []) => {
  const savedKeys = getPrimaryProfileItemKeys(source);
  const seen = new Set();
  return suggestions.filter((item) => {
    const chapterId = clean(item.profileChapterId);
    const key = clean(item.content || item.summary || item.title).toLowerCase();
    if (
      ![
        CAPTURE_DESTINATIONS.HEALTH_PROFILE,
        CAPTURE_DESTINATIONS.CONTACTS,
        CAPTURE_DESTINATIONS.PROFILE,
        CAPTURE_DESTINATIONS.CARE_KNOWLEDGE,
      ].includes(getCaptureSuggestionDestination(item))
      || savedKeys.has(item.clientId || item.id)
      || !chapterId
      || !key
      || seen.has(key)
      || !getProfilePlacement(chapterId)
      || isAllergySuggestion(item)
      || item.archived === true
      || item.hidden === true
      || NON_ACTIONABLE_REVIEW_STATUSES.has(clean(
        item.reviewStatus
        || item.classificationStatus
        || item.deduplicationStatus
      ).toLowerCase())
    ) return false;
    seen.add(key);
    return true;
  });
};

const getInitialDecisions = (source = {}) => (
  source.directProfileReviewDecisions && typeof source.directProfileReviewDecisions === 'object'
    ? source.directProfileReviewDecisions
    : {}
);

const buildChapterCounts = (items = [], decisions = {}) => items.reduce((counts, item) => {
  if (decisions[item.clientId] !== 'add') return counts;
  const label = chapterLabel(item.profileChapterId);
  return { ...counts, [label]: (counts[label] || 0) + 1 };
}, {});

const buildSavedItemReceipt = (savedItem = {}, item = {}) => ({
  id: savedItem.id,
  clientId: item.clientId,
  knowledgeType: savedItem.knowledgeType || item.knowledgeType,
  title: savedItem.title || item.title,
  content: savedItem.summary || item.content || item.summary,
  summary: savedItem.summary || item.content || item.summary,
  sourceText: savedItem.sourceText || item.sourceText,
  confidence: savedItem.confidence || item.confidence,
  profileChapterId: savedItem.profileChapterId || item.profileChapterId,
});

const DirectProfileOrganizationReview = ({
  source = {},
  suggestions = [],
  onComplete,
}) => {
  const reviewItems = useMemo(
    () => getDirectProfileReviewItems(source, suggestions),
    [source, suggestions]
  );
  const [decisions, setDecisions] = useState(() => getInitialDecisions(source));
  const [savingId, setSavingId] = useState('');
  const [error, setError] = useState('');
  const [savedItems, setSavedItems] = useState(() => (
    Array.isArray(source.careKnowledgeSavedItems) ? source.careKnowledgeSavedItems : []
  ));
  const reviewedCount = reviewItems.filter((item) => decisions[item.clientId]).length;
  const isComplete = reviewItems.length > 0 && reviewedCount === reviewItems.length;
  const addedCount = reviewItems.filter((item) => decisions[item.clientId] === 'add').length;
  const originatingChapterId = source.sourceMetadata?.profileChapterId
    || source.metadata?.source?.profileChapterId
    || source.profileChapterId
    || '';
  const groups = useMemo(() => {
    const grouped = new Map();
    reviewItems.forEach((item) => {
      const chapterId = clean(item.profileChapterId);
      if (!grouped.has(chapterId)) grouped.set(chapterId, []);
      grouped.get(chapterId).push(item);
    });
    return [...grouped.entries()];
  }, [reviewItems]);

  const decide = async (item, decision) => {
    const id = item.clientId;
    if (decisions[id]) return;
    setSavingId(id);
    setError('');
    try {
      let savedItem = null;
      let nextSavedItems = savedItems;
      if (decision === 'add') {
        const sourceTitle = source.title
          || source.sourceTitle
          || source.sourceMetadata?.fileName
          || 'Typed care info';
        savedItem = await saveKnowledgeItem({
          childId: source.childId || '',
          captureId: source.id || '',
          knowledgeType: item.knowledgeType || 'other',
          title: item.title,
          summary: item.content || item.summary,
          sourceText: item.sourceText,
          profileField: item.profileField || '',
          profileChapterId: item.profileChapterId,
          profilePlacementMode: PROFILE_PLACEMENT_MODES.EXPLICIT_CAREGIVER,
          sourceRefs: [{
            ...getCareSourceAttribution(source),
            sourceVersion: source.staffOriginalVersion ? (source.sourceLifecycle?.version || 'initial') : 'initial',
            sourceId: source.id || '', sourceTitle, sourceType: source.sourceType || 'care_knowledge',
            sourceContext: 'knowledge', sourceCollection: 'careSources', sourceText: item.sourceText || '',
          }],
          extractionVersion: 'profile_intent_organization_v1',
          capturedBy: { sourceDocumentId: source.id || '', sourceContext: 'knowledge', authorship: 'ai_derived_caregiver_confirmed' },
          evidenceIdentity: { sourceCollection: 'careSources', sourceId: source.id || '', sourceItemId: id },
        });
        nextSavedItems = [
          ...savedItems.filter((candidate) => candidate.clientId !== id),
          buildSavedItemReceipt(savedItem, item),
        ];
      }
      const nextDecisions = { ...decisions, [id]: decision };
      await saveDirectProfileReviewProgress(source.id, {
        decisions: nextDecisions,
        savedItems: nextSavedItems,
      });
      setDecisions(nextDecisions);
      setSavedItems(nextSavedItems);
    } catch (saveError) {
      setError(saveError?.message || "We couldn't save that suggestion right now.");
    } finally {
      setSavingId('');
    }
  };

  const undoSkip = async (item) => {
    const id = item.clientId;
    if (decisions[id] !== 'skip') return;
    setSavingId(id);
    setError('');
    try {
      const nextDecisions = { ...decisions };
      delete nextDecisions[id];
      await saveDirectProfileReviewProgress(source.id, {
        decisions: nextDecisions,
        savedItems,
      });
      setDecisions(nextDecisions);
    } catch (saveError) {
      setError(saveError?.message || "We couldn't undo that decision right now.");
    } finally {
      setSavingId('');
    }
  };

  const finishReview = async () => {
    setSavingId('complete');
    setError('');
    try {
      await markCareKnowledgeSourceReviewComplete(source.id, { savedItems });
      onComplete?.({
        addedCount,
        chapterCounts: buildChapterCounts(reviewItems, decisions),
        originatingChapterId,
        originatingChapterLabel: chapterLabel(originatingChapterId),
      });
    } catch (saveError) {
      setError(saveError?.message || "We couldn't finish this review right now.");
    } finally {
      setSavingId('');
    }
  };

  if (!reviewItems.length) return null;
  return (
    <Box aria-label="Profile organization suggestions">
      <Stack spacing={1.15}>
        {groups.map(([chapterId, items]) => (
          <CollapsibleReviewGroup
            key={chapterId}
            label={chapterLabel(chapterId)}
            count={items.length}
            reviewedCount={items.filter((item) => decisions[item.clientId]).length}
            destinationId={`review-destination-${chapterId}`}
          >
            {items.map((item) => {
              const decision = decisions[item.clientId];
              return (
                <CompactReviewRow
                  source={source}
                  key={item.clientId}
                  text={item.content || item.summary || item.title}
                  destination={chapterLabel(item.profileChapterId)}
                  reason={item.reason || item.extractionReason}
                  originalText={item.sourceText}
                  context={item.supportingContext || item.context}
                  decision={decision}
                  disabled={Boolean(savingId)}
                  onQuickAdd={() => decide(item, 'add')}
                  onSkip={() => decide(item, 'skip')}
                  onUndo={() => undoSkip(item)}
                />
              );
            })}
          </CollapsibleReviewGroup>
        ))}
        {isComplete ? (
          <Button
            variant="contained"
            disabled={Boolean(savingId)}
            onClick={finishReview}
            sx={{ textTransform: 'none', fontWeight: 850 }}
          >
            Done
          </Button>
        ) : null}
        {error ? <Typography color="error">{error}</Typography> : null}
      </Stack>
    </Box>
  );
};

export default DirectProfileOrganizationReview;
